import React from 'react';
import { useGameStore } from '../../store/gameStore';
import { useTranslation } from '../../hooks/useTranslation';
import { BuildingModal } from './BuildingModal';

interface MageTowerModalProps {
  onClose: () => void;
}

export const MageTowerModal: React.FC<MageTowerModalProps> = ({ onClose }) => {
  const t = useTranslation();
  const { buildings, resources, gold, spendResource, spendGold, buildBuilding, addPotion, potions } = useGameStore();

  const GREEN_POTION_CRYSTALS = 5;
  const GREEN_POTION_GOLD = 20;
  const RED_POTION_CRYSTALS = 10;
  const RED_POTION_GOLD = 40;

  const handleBuyGreenPotion = () => {
    if (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) {
      if (spendResource('crystals', GREEN_POTION_CRYSTALS) && spendGold(GREEN_POTION_GOLD)) {
        addPotion('greenPotion', 1);
      }
    }
  };

  const handleBuyRedPotion = () => {
    if (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) {
      if (spendResource('crystals', RED_POTION_CRYSTALS) && spendGold(RED_POTION_GOLD)) {
        addPotion('redPotion', 1);
      }
    }
  };

  const handleBuild = () => {
    if (!buildings.mageTower) {
      buildBuilding('mageTower');
    }
  };

  return (
    <BuildingModal
      title={t.modals.mageTower.title}
      onClose={onClose}
      content={
        <div className="text-white font-pixel text-xs space-y-4">
          {!buildings.mageTower ? (
            <div className="flex flex-col items-center justify-center py-12">
              <img
                src="./sprites/buildings/mageTower.png"
                alt={t.base.mageTower}
                className="w-24 h-24 mb-6 object-contain filter drop-shadow-[0_0_8px_rgba(255,255,255,0.8)]"
                onError={(e) => {
                  (e.target as any).style.display = 'none';
                  const el = (e.target as any).parentElement;
                  if (!el.getAttribute('data-has-emoji')) {
                    el.setAttribute('data-has-emoji', 'true');
                    el.insertAdjacentHTML('afterbegin', '<div class="text-6xl mb-6">🧙‍♂️</div>');
                  }
                }}
              />
              <div className="mb-6 text-gray-400 uppercase tracking-widest text-sm">{t.modals.mageTower.notBuilt}</div>
              <button
                onClick={handleBuild}
                disabled={resources.crystals < 30}
                style={{
                  backgroundColor: resources.crystals >= 30 ? '#ca8a04' : '#374151',
                  color: resources.crystals >= 30 ? '#000' : '#9ca3af',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  fontFamily: '"Press Start 2P", cursive, sans-serif',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: resources.crystals >= 30 ? 'pointer' : 'not-allowed',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: resources.crystals >= 30 ? '0 4px 0 #854d0e' : 'none',
                  transition: 'none'
                }}
                onMouseDown={(e) => {
                  if (resources.crystals >= 30) {
                    e.currentTarget.style.transform = 'translateY(2px)';
                    e.currentTarget.style.boxShadow = '0 2px 0 #854d0e';
                  }
                }}
                onMouseUp={(e) => {
                  if (resources.crystals >= 30) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 0 #854d0e';
                  }
                }}
              >
                {t.modals.mageTower.build} {t.modals.mageTower.buildCost}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-yellow-600 mb-2">{t.modals.mageTower.potions}</div>
              <div className="space-y-3">
                {/* Зелёное зелье */}
                <div className="bg-black/40 p-3 border border-white/5 rounded flex flex-col">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-16 h-16 bg-black/60 border border-white/10 flex items-center justify-center rounded p-1 flex-shrink-0">
                      <span className="text-3xl">💚</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-green-400 text-[10px] mb-1 uppercase">{t.modals.mageTower.greenPotion}</div>
                      <div className="text-[7px] text-white/40 mb-2">{t.modals.mageTower.greenPotionDesc}</div>
                      <div className="text-[7px] text-white/60">{t.modals.mageTower.greenPotionEffect}</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-auto pt-2 border-t border-white/5">
                    <div className="text-[8px] text-yellow-600/70 uppercase">{t.modals.mageTower.stock} {potions?.greenPotion || 0}</div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={handleBuyGreenPotion}
                        disabled={resources.crystals < GREEN_POTION_CRYSTALS || gold < GREEN_POTION_GOLD}
                        style={{
                          backgroundColor: (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) ? '#ca8a04' : '#374151',
                          color: (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) ? '#000' : '#9ca3af',
                          padding: '0.5rem 1rem',
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          fontFamily: '"Press Start 2P", cursive, sans-serif',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) ? 'pointer' : 'not-allowed',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          boxShadow: (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) ? '0 3px 0 #854d0e' : 'none',
                          transition: 'none'
                        }}
                        onMouseDown={(e) => {
                          if (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) {
                            e.currentTarget.style.transform = 'translateY(1.5px)';
                            e.currentTarget.style.boxShadow = '0 1.5px 0 #854d0e';
                          }
                        }}
                        onMouseUp={(e) => {
                          if (resources.crystals >= GREEN_POTION_CRYSTALS && gold >= GREEN_POTION_GOLD) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
                          }
                        }}
                      >
                        {t.modals.mageTower.buy}
                      </button>
                      <div className="flex flex-col items-end gap-0.5 text-[8px]">
                        <div className={resources.crystals >= GREEN_POTION_CRYSTALS ? 'text-cyan-400' : 'text-red-500'}>
                          💎 {GREEN_POTION_CRYSTALS}
                        </div>
                        <div className={gold >= GREEN_POTION_GOLD ? 'text-yellow-500' : 'text-red-500'}>
                          🪙 {GREEN_POTION_GOLD}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Красное зелье */}
                <div className="bg-black/40 p-3 border border-white/5 rounded flex flex-col">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-16 h-16 bg-black/60 border border-white/10 flex items-center justify-center rounded p-1 flex-shrink-0">
                      <span className="text-3xl">❤️</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-red-400 text-[10px] mb-1 uppercase">{t.modals.mageTower.redPotion}</div>
                      <div className="text-[7px] text-white/40 mb-2">{t.modals.mageTower.redPotionDesc}</div>
                      <div className="text-[7px] text-white/60">{t.modals.mageTower.redPotionEffect}</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-auto pt-2 border-t border-white/5">
                    <div className="text-[8px] text-yellow-600/70 uppercase">{t.modals.mageTower.stock} {potions?.redPotion || 0}</div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={handleBuyRedPotion}
                        disabled={resources.crystals < RED_POTION_CRYSTALS || gold < RED_POTION_GOLD}
                        style={{
                          backgroundColor: (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) ? '#ca8a04' : '#374151',
                          color: (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) ? '#000' : '#9ca3af',
                          padding: '0.5rem 1rem',
                          fontSize: '0.6rem',
                          fontWeight: 'bold',
                          fontFamily: '"Press Start 2P", cursive, sans-serif',
                          border: 'none',
                          borderRadius: '0.25rem',
                          cursor: (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) ? 'pointer' : 'not-allowed',
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          boxShadow: (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) ? '0 3px 0 #854d0e' : 'none',
                          transition: 'none'
                        }}
                        onMouseDown={(e) => {
                          if (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) {
                            e.currentTarget.style.transform = 'translateY(1.5px)';
                            e.currentTarget.style.boxShadow = '0 1.5px 0 #854d0e';
                          }
                        }}
                        onMouseUp={(e) => {
                          if (resources.crystals >= RED_POTION_CRYSTALS && gold >= RED_POTION_GOLD) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 3px 0 #854d0e';
                          }
                        }}
                      >
                        {t.modals.mageTower.buy}
                      </button>
                      <div className="flex flex-col items-end gap-0.5 text-[8px]">
                        <div className={resources.crystals >= RED_POTION_CRYSTALS ? 'text-cyan-400' : 'text-red-500'}>
                          💎 {RED_POTION_CRYSTALS}
                        </div>
                        <div className={gold >= RED_POTION_GOLD ? 'text-yellow-500' : 'text-red-500'}>
                          🪙 {RED_POTION_GOLD}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};



